import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Anchor, DollarSign, TrendingDown, AlertTriangle, Zap, RefreshCw,
  Plug, Loader2, Server, HardDrive, Camera, Database as DbIcon,
  Network, CheckCircle, Lightbulb, ChevronRight, Sparkles,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend,
} from 'recharts';
import DigitalOceanConnect, { useDigitalOceanCredentials } from '../components/DigitalOceanConnect';
import EmptyDataState from '../components/EmptyDataState';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0080FF';
const BRAND_DARK = '#0066CC';

// Demo fixture so the page renders meaningfully on the demo tenant
const DEMO_DATA = {
  provider: 'DigitalOcean',
  account_id: 'de***@demo.cartieai.com',
  resources_scanned: 47,
  total_monthly_spend: 3142.50,
  waste_found: 612.30,
  waste_percent: 19.5,
  resource_breakdown: {
    droplets: 18,
    volumes: 12,
    snapshots: 9,
    databases: 4,
    load_balancers: 4,
  },
  savings_breakdown: [
    { category: 'Orphan / Detached Volumes', count: 3, monthly_savings: 84.00, items: ['3 detached volume(s) totaling 840 GB', 'Delete or reattach — currently billed at $0.10/GB/mo'] },
    { category: 'Stale Snapshots', count: 9, monthly_savings: 28.80, items: ['9 snapshot(s) totaling 960 GB', 'Review for snapshots older than 30 days'] },
    { category: 'Droplet Right-Sizing', count: 18, monthly_savings: 421.20, items: ['Typical savings: 15% from right-sizing underutilized Droplets', 'Enable monitoring + use lower tier for <20% CPU Droplets'] },
    { category: 'Managed Database Optimization', count: 4, monthly_savings: 78.30, items: ['Schedule downscale on dev/staging DBs outside business hours', 'Move dev workloads to smallest plan'] },
  ],
  quick_wins: [
    { action: 'Delete 3 detached volume(s)', savings: 84.00, effort: '5 min', risk: 'Low' },
    { action: 'Review snapshots older than 30 days', savings: 28.80, effort: '15 min', risk: 'Low' },
    { action: 'Right-size underutilized Droplets', savings: 421.20, effort: '30 min', risk: 'Low' },
  ],
  note: 'Demo data — connect your DigitalOcean account to see real numbers.',
};

const KPI = ({ icon: Icon, label, value, sub, tone = 'slate' }) => {
  const palette = {
    blue:    'border-emerald-200 bg-emerald-50 dark:bg-blue-950/30 text-blue-900 dark:text-blue-200',
    red:     'border-red-200 bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-200',
    emerald: 'border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-950/30 text-emerald-100 dark:text-emerald-200',
    slate:   'border-emerald-500/20 bg-slate-900 text-white dark:text-slate-100',
  };
  return (
    <Card className={`${palette[tone]} border`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-70 mb-1">{label}</div>
            <div className="text-2xl font-bold">{value}</div>
            {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
          </div>
          <Icon className="w-5 h-5 opacity-60" />
        </div>
      </CardContent>
    </Card>
  );
};

const DigitalOceanDashboard = () => {
  const [showConnect, setShowConnect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [realData, setRealData] = useState(null);
  const { credentials, isConnected, disconnect, updateCredentials } = useDigitalOceanCredentials();

  // If connected, fetch real audit
  const fetchRealData = async () => {
    if (!credentials) return;
    setLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/api/cloud-audit/digitalocean`, credentials);
      // Augment payload with resource_breakdown derived from `note` (fallback)
      const data = resp.data;
      if (!data.resource_breakdown) {
        // Parse from `note` string: "X Droplets · Y Volumes · Z Snapshots · ..."
        const note = data.note || '';
        const parse = (label) => {
          const m = note.match(new RegExp(`(\\d+)\\s+${label}`));
          return m ? parseInt(m[1], 10) : 0;
        };
        data.resource_breakdown = {
          droplets: parse('Droplets'),
          volumes: parse('Volumes'),
          snapshots: parse('Snapshots'),
          databases: parse('Databases'),
          load_balancers: parse('LBs'),
        };
      }
      setRealData(data);
      toast.success('Audit refreshed from DigitalOcean');
    } catch (error) {
      console.error('DO audit failed:', error);
      toast.error(error.response?.data?.detail || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected && credentials) fetchRealData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  const handleConnected = (creds, payload) => {
    updateCredentials(creds, true);
    setRealData(payload);
    setShowConnect(false);
  };

  const data = realData || DEMO_DATA;
  const isDemo = !realData;
  const breakdown = data.resource_breakdown || {};
  const resourceItems = [
    { label: 'Droplets', value: breakdown.droplets || 0, icon: Server },
    { label: 'Volumes', value: breakdown.volumes || 0, icon: HardDrive },
    { label: 'Snapshots', value: breakdown.snapshots || 0, icon: Camera },
    { label: 'Databases', value: breakdown.databases || 0, icon: DbIcon },
    { label: 'Load Balancers', value: breakdown.load_balancers || 0, icon: Network },
  ];

  // Pie data
  const pieData = useMemo(() => (data.savings_breakdown || []).map(s => ({
    name: s.category,
    value: s.monthly_savings,
  })).filter(d => d.value > 0), [data]);

  const PIE_COLORS = ['#0080FF', '#0066CC', '#33A1FF', '#80C0FF', '#0050A0'];

  return (
    <div className="min-h-screen bg-white text-slate-900 p-6" data-testid="digitalocean-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: BRAND }}>
              <Anchor className="w-7 h-7 text-white drop-shadow-sm" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl font-bold text-slate-900">DigitalOcean</h1>
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-widest uppercase bg-amber-500/15 border border-amber-500/30 text-amber-400 dark:text-amber-300"
                  data-testid="do-beta-badge"
                  title="Audit + dashboard ready · live cost streaming coming in Q2 2026 if customer demand exists"
                >
                  BETA
                </span>
              </div>
              <p className="text-sm text-slate-600">
                {isDemo
                  ? 'Demo data — connect your account to audit real resources.'
                  : `Live · ${data.account_id} · ${data.resources_scanned} resources scanned`}
              </p>
              <p className="text-xs text-amber-300 dark:text-amber-400/80 mt-1">
                Currently in beta: audit + waste-detection + dashboard only. Live cost streaming + auto-rightsizing coming Q2 2026 based on pilot demand.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isConnected ? (
              <Button onClick={() => setShowConnect(true)} data-testid="do-connect-btn"
                style={{ backgroundColor: BRAND, color: 'white' }}>
                <Plug className="w-4 h-4 mr-2" /> Connect DigitalOcean
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={fetchRealData} disabled={loading} data-testid="do-refresh">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Refresh
                </Button>
                <Button variant="ghost" onClick={() => { disconnect(); setRealData(null); toast.info('Disconnected'); }}
                  data-testid="do-disconnect" className="text-red-600">
                  Disconnect
                </Button>
              </>
            )}
          </div>
        </div>

        {isDemo && (
          <div className="bg-emerald-50 dark:bg-blue-950/30 border border-emerald-200 dark:border-blue-800 rounded-md p-3 text-sm text-blue-900 dark:text-blue-200 flex items-center gap-2" data-testid="do-demo-banner">
            <Sparkles className="w-4 h-4" />
            You are viewing <strong>demo data</strong>. Click <em>Connect DigitalOcean</em> with a read-only PAT to see your real account.
          </div>
        )}

        {/* KPI band */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={DollarSign} label="Est. Monthly Spend" value={`$${(data.total_monthly_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} tone="blue" sub="droplets + volumes + snapshots + db + lb" />
          <KPI icon={TrendingDown} label="Waste Found" value={`$${(data.waste_found || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} tone="red" sub={`${data.waste_percent || 0}% of monthly spend`} />
          <KPI icon={Zap} label="Resources Scanned" value={data.resources_scanned || 0} tone="emerald" sub="droplets + volumes + snapshots + db + lb" />
          <KPI icon={CheckCircle} label="Quick Wins" value={data.quick_wins?.length || 0} tone="slate" sub="actionable today" />
        </div>

        {/* Resource breakdown + Savings pie */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Server className="w-5 h-5" /> Resource Inventory</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="do-resource-list">
                {resourceItems.map(r => (
                  <div key={r.label} className="flex items-center justify-between p-3 rounded-md bg-slate-900 text-slate-100 dark:bg-slate-900 border border-emerald-500/20">
                    <div className="flex items-center gap-3">
                      <r.icon className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium">{r.label}</span>
                    </div>
                    <Badge variant="secondary" className="text-base px-3">{r.value}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Savings Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <RechartsPie>
                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={85} label={({ value }) => `$${Math.round(value)}`}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}/mo`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <EmptyDataState message="No waste detected — well done." />
              )}
            </CardContent>
          </Card>

        </div>

        {/* Detailed savings table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" /> Detailed Savings Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto" data-testid="do-savings-table">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-600">
                  <tr><th className="py-2 px-2">Category</th><th className="py-2 px-2">Count</th><th className="py-2 px-2 text-right">Monthly Savings</th><th className="py-2 px-2">Action</th></tr>
                </thead>
                <tbody>
                  {(data.savings_breakdown || []).map((s, i) => (
                    <tr key={i} className="border-t border-emerald-500/20">
                      <td className="py-3 px-2 font-medium">{s.category}</td>
                      <td className="py-3 px-2">{s.count}</td>
                      <td className="py-3 px-2 text-right font-semibold text-emerald-300 dark:text-emerald-400">${s.monthly_savings.toFixed(2)}</td>
                      <td className="py-3 px-2 text-xs text-slate-600">{s.items?.[1] || s.items?.[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Quick wins */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Quick Wins You Can Take Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="do-quick-wins">
              {(data.quick_wins || []).map((qw, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-md bg-amber-500/10 dark:bg-amber-950/30 border border-amber-500/30 dark:border-amber-800">
                  <ChevronRight className="w-4 h-4 text-amber-300 dark:text-amber-300 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-amber-900 dark:text-amber-100">{qw.action}</div>
                    <div className="text-xs text-amber-300 dark:text-amber-300 mt-0.5">
                      Saves <strong>${qw.savings.toFixed(2)}/mo</strong> · {qw.effort} · risk: {qw.risk}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer note */}
        <div className="text-xs text-slate-600 italic">{data.note}</div>

        <DigitalOceanConnect
          isOpen={showConnect}
          onClose={() => setShowConnect(false)}
          onConnected={handleConnected}
        />
      </div>
    </div>
  );
};

export default DigitalOceanDashboard;
